// OWMap BSL1 — Right story panel + bottom playback bar
const { useState: useStateRight, useEffect: useEffectRight, useCallback: useCallbackRight } = React;

const PAGE_SIZE = 8;

// ── Skeleton loader ──────────────────────────────────────────────────────────
function StorySkeleton() {
  return (
    <div className="story-skeleton">
      {[1, 2, 3].map(i => (
        <div key={i} className="sk-card">
          <div className="sk-line sk-line-title"></div>
          <div className="sk-line sk-line-meta"></div>
          <div className="sk-line sk-line-excerpt"></div>
        </div>
      ))}
    </div>
  );
}

// ── Right Panel router ───────────────────────────────────────────────────────
function RightPanel({ selectedCoord, currentStory, isPlaying, onPlay }) {
  if (!selectedCoord) return <RightDefault onPlay={onPlay} />;
  return <RightSelected coord={selectedCoord} currentStory={currentStory} isPlaying={isPlaying} onPlay={onPlay} />;
}

// ── Default (no coord selected) ──────────────────────────────────────────────
function RightDefault({ onPlay }) {
  const featured = [
    COORDS.find(c => c.id === "dunhuang"),
    COORDS.find(c => c.id === "reykjavik"),
    COORDS.find(c => c.id === "timbuktu"),
  ].filter(Boolean);

  const [idx, setIdx] = useStateRight(0);
  useEffectRight(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % featured.length), 8000);
    return () => clearInterval(t);
  }, []);

  const c = featured[idx];
  const story = getStories(c.id)[0];

  return (
    <aside className="right-panel">
      <div className="right-head">
        <div className="right-title-fr">Tonight,</div>
        <div className="right-title-cn">你想去哪里？</div>
        <div className="right-sub">全球 5,420 个坐标 · 每晚 23 个新故事</div>
      </div>
      <div className="featured-card">
        <div className="featured-pic" style={{ background: `linear-gradient(135deg, ${CULTURES[c.culture].color}33 0%, transparent 80%)` }}>
          <div className="featured-pic-inner" style={{ background: `radial-gradient(circle at 50% 60%, ${CULTURES[c.culture].color}55 0%, transparent 70%)` }}></div>
          <div className="featured-coord-meta">
            <div className="culture-tag" style={{ borderColor: CULTURES[c.culture].color, color: CULTURES[c.culture].color }}>{CULTURES[c.culture].label}</div>
            <div className="featured-coord-tech">{c.lat.toFixed(2)}° · {c.lng.toFixed(2)}°</div>
          </div>
        </div>
        <div className="featured-body">
          <div className="featured-name">{c.name}</div>
          <div className="featured-region">{c.region}</div>
          <div className="featured-story">「{story.title}」</div>
          <div className="featured-excerpt">{story.excerpt}</div>
          <div className="featured-actions">
            <button className="play-pill" onClick={() => onPlay(story, c)}>
              <svg viewBox="0 0 24 24" width="12" height="12"><path d="M8 6 L18 12 L8 18 Z" fill="currentColor"/></svg>
              <span>播放 · {story.duration}</span>
            </button>
            <div className="featured-dots">
              {featured.map((_, i) => <span key={i} className={`fdot ${i === idx ? "fdot-on" : ""}`}></span>)}
            </div>
          </div>
        </div>
      </div>

      <div className="right-block">
        <div className="block-head">
          <span>今夜推荐</span>
          <button className="link-btn">更多 →</button>
        </div>
        <RecsList onPlay={onPlay} />
      </div>
    </aside>
  );
}

// ── Recommendations list ─────────────────────────────────────────────────────
function RecsList({ onPlay }) {
  const recs = [
    { coord: COORDS.find(c => c.id === "petra"),    idx: 0 },
    { coord: COORDS.find(c => c.id === "varanasi"), idx: 0 },
    { coord: COORDS.find(c => c.id === "kyoto"),    idx: 0 },
  ].filter(r => r.coord);

  return (
    <div className="recs">
      {recs.map((r, i) => {
        const s = getStories(r.coord.id)[r.idx];
        if (!s) return null;
        return (
          <button key={i} className="rec-row" onClick={() => onPlay(s, r.coord)}>
            <span className="rec-dot" style={{ background: CULTURES[r.coord.culture].color, boxShadow: `0 0 8px ${CULTURES[r.coord.culture].color}` }}></span>
            <div className="rec-text">
              <div className="rec-title">{s.title}</div>
              <div className="rec-meta">{r.coord.name} · {s.duration} · {s.genre}</div>
            </div>
            <svg className="rec-play" viewBox="0 0 24 24" width="14" height="14"><path d="M8 6 L18 12 L8 18 Z" fill="currentColor"/></svg>
          </button>
        );
      })}
    </div>
  );
}

// ── Story genre/style inline filter ─────────────────────────────────────────
function InlineStoryFilter({ genreFilter, setGenreFilter }) {
  const genres = [
    { id: 'folk',    label: '民间' },
    { id: 'myth',    label: '神话' },
    { id: 'history', label: '历史' },
    { id: 'nature',  label: '自然' },
    { id: 'dream',   label: '梦境' },
  ];
  return (
    <div className="inline-filter">
      {genres.map(g => (
        <button
          key={g.id}
          className={`inline-chip ${genreFilter.includes(g.id) ? 'chip-on' : ''}`}
          onClick={() => setGenreFilter(prev =>
            prev.includes(g.id) ? prev.filter(x => x !== g.id) : [...prev, g.id]
          )}
        >{g.label}</button>
      ))}
      {genreFilter.length > 0 && (
        <button className="inline-chip-clear" onClick={() => setGenreFilter([])}>全部</button>
      )}
    </div>
  );
}

// ── Selected coord panel (async stories) ────────────────────────────────────
function RightSelected({ coord, currentStory, isPlaying, onPlay }) {
  const [stories, setStories]         = useStateRight([]);
  const [loading, setLoading]         = useStateRight(true);
  const [page, setPage]               = useStateRight(1);
  const [hasMore, setHasMore]         = useStateRight(true);
  const [genreFilter, setGenreFilter] = useStateRight([]);

  const culture = CULTURES[coord.culture];

  useEffectRight(() => {
    let cancelled = false;
    setLoading(true);
    setPage(1);
    setStories([]);

    StoryCache.get(coord.id)
      .then(data => {
        if (cancelled) return;
        const arr = Array.isArray(data) ? data : (data.stories || []);
        setStories(arr);
        setHasMore(arr.length >= PAGE_SIZE);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setStories(getStories(coord.id));
        setLoading(false);
        setHasMore(false);
      });

    return () => { cancelled = true; };
  }, [coord.id]);

  const loadMore = useCallbackRight(() => {
    const coordId = coord.id; // capture at call time — coord prop may change
    const nextPage = page + 1;
    BSL1API.getStories(coordId, { page: nextPage, pageSize: PAGE_SIZE })
      .then(arr => {
        setStories(prev => [...prev, ...arr]);
        setHasMore(arr.length >= PAGE_SIZE);
        setPage(nextPage);
      })
      .catch(() => setHasMore(false));
  }, [coord.id, page]); // coord.id in deps ensures stale closure is always fresh

  const visibleStories = genreFilter.length > 0
    ? stories.filter(s => {
        const genreMap = { folk: '民间故事', myth: '神话传说', history: '历史叙事', nature: '自然奇谈', dream: '梦境故事' };
        return genreFilter.some(g => s.genre === genreMap[g]);
      })
    : stories;

  return (
    <aside className="right-panel">
      <div className="coord-head">
        <div className="coord-tag" style={{ background: `${culture.color}1f`, color: culture.color, borderColor: `${culture.color}55` }}>
          <span className="coord-tag-dot" style={{ background: culture.color }}></span>
          {culture.label}
        </div>
        <div className="coord-name">{coord.name}</div>
        <div className="coord-region">{coord.region}</div>
        <div className="coord-tech">
          <span>{coord.lat.toFixed(4)}°{coord.lat >= 0 ? 'N' : 'S'}</span>
          <span className="coord-tech-dot">·</span>
          <span>{Math.abs(coord.lng).toFixed(4)}°{coord.lng >= 0 ? 'E' : 'W'}</span>
          <span className="coord-tech-dot">·</span>
          <span>{stories.length || coord.count} 个故事</span>
          <span className="coord-tech-dot">·</span>
          <span>热度 {Math.round(coord.heat * 100)}</span>
        </div>
      </div>

      <div className="coord-stat-row">
        <div className="stat">
          <div className="stat-num">{stories.length || coord.count}</div>
          <div className="stat-label">本地故事</div>
        </div>
        <div className="stat">
          <div className="stat-num">{Math.round(coord.heat * 18.4)}m</div>
          <div className="stat-label">总收听</div>
        </div>
        <div className="stat">
          <div className="stat-num">{(coord.heat * 4.7 + 0.2).toFixed(1)}</div>
          <div className="stat-label">睡眠指数</div>
        </div>
      </div>

      <InlineStoryFilter genreFilter={genreFilter} setGenreFilter={setGenreFilter} />

      <div className="story-list">
        {loading ? <StorySkeleton /> : visibleStories.map((s, i) => (
          <article key={s.id} className={`story-card ${currentStory && currentStory.id === s.id ? "story-active" : ""}`}>
            <div className="story-card-head">
              <div className="story-num">{String(i + 1).padStart(2, "0")}</div>
              <div className="story-card-titles">
                <div className="story-title">{s.title}</div>
                <div className="story-tags">
                  <span className="tag tag-genre" style={{ borderColor: `${culture.color}66`, color: culture.color }}>{s.genre}</span>
                  <span className="tag">{s.style}</span>
                  <span className="tag tag-time">
                    <svg viewBox="0 0 24 24" width="9" height="9"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.5"/><path d="M12 7 L12 12 L15.5 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                    {s.duration}
                  </span>
                  {s.paid && <span className="tag tag-paid"><svg viewBox="0 0 24 24" width="9" height="9"><rect x="6" y="11" width="12" height="9" rx="1" fill="currentColor"/><path d="M9 11 V8 a3 3 0 0 1 6 0 V11" fill="none" stroke="currentColor" strokeWidth="1.5"/></svg> 会员</span>}
                </div>
              </div>
              <button className="story-bookmark">
                <svg viewBox="0 0 24 24" width="14" height="14"><path d="M7 4 L17 4 L17 21 L12 17 L7 21 Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
              </button>
            </div>
            <p className="story-excerpt">{s.excerpt}</p>
            <div className="story-foot">
              <span className="story-gen">{s.gen}</span>
              <div className="story-foot-actions">
                <span className="story-plays">{(s.plays / 1000).toFixed(1)}k 次收听</span>
                <button className="story-play" style={{ background: culture.color }} onClick={() => onPlay(s, coord)}>
                  {currentStory && currentStory.id === s.id && isPlaying
                    ? <svg viewBox="0 0 24 24" width="12" height="12"><rect x="7" y="6" width="3.5" height="12" fill="currentColor"/><rect x="13.5" y="6" width="3.5" height="12" fill="currentColor"/></svg>
                    : <svg viewBox="0 0 24 24" width="12" height="12"><path d="M8 6 L18 12 L8 18 Z" fill="currentColor"/></svg>}
                </button>
              </div>
            </div>
          </article>
        ))}

        {!loading && visibleStories.length === 0 && (
          <div className="story-empty">
            <div className="empty-icon">◎</div>
            <div className="empty-text">暂无匹配故事</div>
          </div>
        )}

        {!loading && hasMore && (
          <button className="load-more-btn" onClick={loadMore}>加载更多故事</button>
        )}

        <button className="generate-card">
          <div className="generate-icon"><svg viewBox="0 0 24 24" width="18" height="18"><path d="M12 4 V20 M4 12 H20" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.5"/></svg></div>
          <div className="generate-text">
            <div className="generate-title">为这个坐标生成新故事</div>
            <div className="generate-sub">本月剩余 3 / 5 次 · AI 编织</div>
          </div>
        </button>
      </div>
    </aside>
  );
}

// ── Bottom playback bar ──────────────────────────────────────────────────────
function PlayBar({ currentStory, currentCoord, isPlaying, isBuffering, onToggle, onSeek, progress }) {
  if (!currentStory) return null;

  const culture = CULTURES[currentCoord.culture];
  const [speed, setSpeed] = useStateRight(1.0);
  const speeds = [0.75, 1.0, 1.25, 1.5, 2.0];

  const cycleSpeed = () => {
    const next = speeds[(speeds.indexOf(speed) + 1) % speeds.length];
    setSpeed(next);
    BSL1Audio.setSpeed(next);
  };

  const handleProgressClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width) return; // element hidden or zero-width — skip to avoid NaN
    const ratio = (e.clientX - rect.left) / rect.width;
    if (onSeek) onSeek(Math.max(0, Math.min(1, ratio)));
  };

  return (
    <div className="play-bar">
      <div className="pb-left">
        <span className="pb-coord-dot" style={{ background: culture.color, boxShadow: `0 0 10px ${culture.color}` }}></span>
        <div className="pb-text">
          <div className="pb-title">{currentStory.title}</div>
          <div className="pb-coord">{currentCoord.name} · {currentCoord.region}</div>
        </div>
      </div>
      <div className="pb-center">
        <div className="pb-progress" onClick={handleProgressClick} style={{ cursor: 'pointer' }}>
          <div className="pb-progress-fill" style={{ width: `${progress * 100}%`, background: culture.color }}></div>
          <span className="pb-progress-thumb" style={{ left: `${progress * 100}%`, background: culture.color }}></span>
        </div>
        <div className="pb-times">
          <span>{formatTime(progress * parseDuration(currentStory.duration))}</span>
          <span>{currentStory.duration}</span>
        </div>
      </div>
      <div className="pb-right">
        <button className="pb-icon" title="上一曲"><svg viewBox="0 0 24 24" width="14" height="14"><path d="M6 8 L6 16 M6 12 L13 6 V18 L6 12" fill="currentColor"/></svg></button>
        <button className={`pb-toggle ${isBuffering ? 'pb-buffering' : ''}`} onClick={onToggle}>
          {isBuffering
            ? <svg viewBox="0 0 24 24" width="14" height="14"><circle cx="12" cy="12" r="8" fill="none" stroke="#fff" strokeWidth="2" strokeDasharray="25 13"/></svg>
            : isPlaying
              ? <svg viewBox="0 0 24 24" width="14" height="14"><rect x="6" y="5" width="4" height="14" fill="#fff"/><rect x="14" y="5" width="4" height="14" fill="#fff"/></svg>
              : <svg viewBox="0 0 24 24" width="14" height="14"><path d="M7 5 L19 12 L7 19 Z" fill="#fff"/></svg>}
        </button>
        <button className="pb-icon" title="下一曲"><svg viewBox="0 0 24 24" width="14" height="14"><path d="M18 8 L18 16 M18 12 L11 6 V18 L18 12" fill="currentColor"/></svg></button>
        <button className="pb-icon" title="睡眠定时"><svg viewBox="0 0 24 24" width="14" height="14"><path d="M19 14 A8 8 0 1 1 10 5 A6 6 0 0 0 19 14 Z" fill="currentColor"/></svg></button>
      </div>
    </div>
  );
}

function formatTime(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function parseDuration(str) {
  if (!str) return 0;
  const parts = str.split(':').map(Number);
  return parts.length === 2 ? parts[0] * 60 + parts[1] : 0;
}

window.RightPanel = RightPanel;
window.PlayBar = PlayBar;
